import { useMemo, useState } from 'react';
import type { HistoryResponse, LeagueBundle } from '../../shared/types';
import { Avatar, Empty, ErrorState, Loading, PageHead, Section } from '../components/common';
import { useScores } from '../lib/api';
import { fmtPoints, fmtPct } from '../lib/format';
import {
  buildRivalries,
  lookup,
  managerDirectory,
  overallRecord,
  uniquePairings,
  type HeadToHead,
} from '../lib/rivalries';

function recordText(r: HeadToHead): string {
  return r.ties > 0 ? `${r.wins}-${r.losses}-${r.ties}` : `${r.wins}-${r.losses}`;
}

function winPctOf(r: HeadToHead): number {
  return r.games === 0 ? 0 : (r.wins + r.ties * 0.5) / r.games;
}

/** Short handle for the matrix header, where horizontal room is scarce. */
function short(name: string): string {
  return name.length <= 10 ? name : `${name.slice(0, 9)}…`;
}

export default function Rivalries({
  bundle,
  history,
}: {
  bundle: LeagueBundle;
  history: HistoryResponse | null;
}) {
  const [includePlayoffs, setIncludePlayoffs] = useState(true);

  // Every season the league has played: the one in progress plus the archive.
  const leagueIds = useMemo(() => {
    const ids = [bundle.league.league_id];
    for (const s of history?.seasons ?? []) ids.push(s.league_id);
    return ids;
  }, [bundle.league.league_id, history]);

  const { data, error, loading, reload } = useScores(leagueIds);

  const dir = useMemo(() => managerDirectory(bundle, history), [bundle, history]);

  const table = useMemo(
    () => buildRivalries(data ?? [], includePlayoffs),
    [data, includePlayoffs],
  );

  // Only managers who have actually played a game belong in the matrix.
  const managers = useMemo(() => {
    const played = new Set<string>();
    for (const k of table.keys()) played.add(k.split('|')[0]);
    return [...dir.values()]
      .filter((m) => played.has(m.user_id))
      .sort((a, b) => {
        const ra = overallRecord(table, a.user_id);
        const rb = overallRecord(table, b.user_id);
        return winPctOf(rb) - winPctOf(ra) || rb.pointsFor - ra.pointsFor;
      });
  }, [dir, table]);

  const pairings = useMemo(() => uniquePairings(table, dir), [table, dir]);

  const closest = useMemo(
    () => [...pairings].sort((a, b) => a.avgMargin - b.avgMargin).slice(0, 5),
    [pairings],
  );

  const lopsided = useMemo(
    () =>
      [...pairings]
        .filter((p) => p.record.games >= 2)
        .sort((a, b) => {
          const da = Math.abs(a.record.wins - a.record.losses) / a.record.games;
          const db = Math.abs(b.record.wins - b.record.losses) / b.record.games;
          return db - da || b.record.games - a.record.games;
        })
        .slice(0, 5),
    [pairings],
  );

  if (loading) return <Loading label="Loading every game ever played" />;
  if (error || !data) {
    return <ErrorState message={error ?? 'No score data returned'} onRetry={reload} />;
  }

  const totalGames = data.reduce((n, s) => n + s.games.length, 0);
  const playedSeasons = data.filter((s) => s.games.length > 0).length;

  if (totalGames === 0) {
    return (
      <>
        <PageHead title="Rivalries" />
        <div className="card">
          <Empty>No games played yet.</Empty>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHead
        title="Rivalries"
        meta={[
          `${totalGames} games`,
          playedSeasons === 1 ? 'one season' : `${playedSeasons} seasons`,
          'By manager, not roster',
        ]}
      />

      <div className="toolbar">
        <button
          className={`chip ${includePlayoffs ? 'on' : ''}`}
          onClick={() => setIncludePlayoffs(true)}
        >
          All games
        </button>
        <button
          className={`chip ${!includePlayoffs ? 'on' : ''}`}
          onClick={() => setIncludePlayoffs(false)}
        >
          Regular season only
        </button>
      </div>

      <Section
        title="Head-to-Head Grid"
        hint="Row's record against column"
      >
        <div className="table-scroll">
          <table className="h2h">
            <thead>
              <tr>
                <th className="h2h-corner">Manager</th>
                {managers.map((m) => (
                  <th key={m.user_id} className="h2h-col" title={m.name}>
                    {short(m.name)}
                  </th>
                ))}
                <th className="h2h-col">All-time</th>
              </tr>
            </thead>
            <tbody>
              {managers.map((row) => {
                const overall = overallRecord(table, row.user_id);
                return (
                  <tr key={row.user_id}>
                    <td className="h2h-row-head">
                      <div className="team-cell">
                        <Avatar id={row.avatar} alt={row.name} />
                        <span className="team-title">{row.name}</span>
                      </div>
                    </td>
                    {managers.map((col) => {
                      if (col.user_id === row.user_id) {
                        return (
                          <td key={col.user_id} className="h2h-self">
                            —
                          </td>
                        );
                      }
                      const rec = lookup(table, row.user_id, col.user_id);
                      if (!rec) {
                        return (
                          <td key={col.user_id} className="faint">
                            ·
                          </td>
                        );
                      }
                      const pct = winPctOf(rec);
                      return (
                        <td
                          key={col.user_id}
                          className={pct > 0.5 ? 'h2h-good' : pct < 0.5 ? 'h2h-bad' : ''}
                          title={`${row.name} vs ${col.name}: ${recordText(rec)} · ${fmtPoints(
                            rec.pointsFor,
                          )} to ${fmtPoints(rec.pointsAgainst)}`}
                        >
                          {recordText(rec)}
                        </td>
                      );
                    })}
                    <td className="h2h-total">{recordText(overall)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="faint" style={{ fontSize: 12, marginTop: 10 }}>
          Green is a winning record. Hover a cell for series points.
        </p>
      </Section>

      <div className="grid grid-2" style={{ marginTop: 30, alignItems: 'start' }}>
        <div>
          <div className="section-head">
            <h2 style={{ fontSize: 19, textTransform: 'uppercase' }}>Closest Rivalries</h2>
            <span className="hint">Smallest average margin</span>
          </div>
          <div className="card">
            {closest.length === 0 ? (
              <Empty>Not enough games yet.</Empty>
            ) : (
              closest.map((r) => (
                <div className="txn" key={`${r.a.user_id}-${r.b.user_id}`}>
                  <div className="rivalry-row">
                    <Avatar id={r.a.avatar} alt={r.a.name} />
                    <span className="rivalry-name">{r.a.name}</span>
                    <span className="rivalry-score tabnum">
                      {recordText(r.record)}
                    </span>
                    <span className="rivalry-name" style={{ textAlign: 'right' }}>
                      {r.b.name}
                    </span>
                    <Avatar id={r.b.avatar} alt={r.b.name} />
                  </div>
                  <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
                    {r.record.games} meeting{r.record.games === 1 ? '' : 's'} · decided by{' '}
                    {fmtPoints(r.avgMargin)} on average
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div>
          <div className="section-head">
            <h2 style={{ fontSize: 19, textTransform: 'uppercase' }}>Most Lopsided</h2>
            <span className="hint">One-sided series</span>
          </div>
          <div className="card">
            {lopsided.length === 0 ? (
              <Empty>Not enough repeat matchups yet.</Empty>
            ) : (
              lopsided.map((r) => {
                // Present it from the winning side's perspective so the row
                // reads as a statement rather than a puzzle.
                const flip = r.record.wins < r.record.losses;
                const top = flip ? r.b : r.a;
                const bottom = flip ? r.a : r.b;
                const wins = flip ? r.record.losses : r.record.wins;
                const losses = flip ? r.record.wins : r.record.losses;
                return (
                  <div className="txn" key={`${r.a.user_id}-${r.b.user_id}-lop`}>
                    <div className="rivalry-row">
                      <Avatar id={top.avatar} alt={top.name} />
                      <span className="rivalry-name">{top.name}</span>
                      <span className="rivalry-score tabnum good">
                        {wins}-{losses}
                      </span>
                      <span className="rivalry-name" style={{ textAlign: 'right' }}>
                        {bottom.name}
                      </span>
                      <Avatar id={bottom.avatar} alt={bottom.name} />
                    </div>
                    <div className="faint" style={{ fontSize: 12, marginTop: 6 }}>
                      {fmtPct(wins / Math.max(1, r.record.games), 0)} win rate over{' '}
                      {r.record.games} meeting{r.record.games === 1 ? '' : 's'}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </>
  );
}
